import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Search, Plus, Phone, ChevronRight, Building2, MessageCircle } from "lucide-react";

function normalizePhoneForWa(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("33")) return digits;
  if (digits.startsWith("0")) return "33" + digits.slice(1);
  return digits;
}

export default function ClientsList() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return clients;
    const s = q.toLowerCase();
    return clients.filter(
      (c) =>
        c.company_name.toLowerCase().includes(s) ||
        (c.contact_name || "").toLowerCase().includes(s) ||
        (c.city || "").toLowerCase().includes(s) ||
        (c.address || "").toLowerCase().includes(s),
    );
  }, [clients, q]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Clients</h1>
        <Link
          to="/clients/new"
          data-testid="new-client-btn"
          className="inline-flex items-center gap-2 font-bold px-4 py-2.5 rounded-full transition-all active:scale-95 shadow-lg hover:opacity-90 bg-[#9DCB2E] text-[#0f3a18]"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Nouveau
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" strokeWidth={2.5} />
        <input
          data-testid="clients-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher..."
          className="w-full bg-white border border-stone-200 outline-none rounded-2xl pl-12 pr-4 py-3 transition-all font-medium focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
        />
      </div>

      {loading ? (
        <div className="text-center text-stone-400 py-10">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-stone-200 p-10 text-center">
          <Building2 className="w-10 h-10 text-stone-300 mx-auto mb-2" strokeWidth={2} />
          <div className="font-heading font-black text-lg">
            {clients.length === 0 ? "Aucun client" : "Aucun résultat"}
          </div>
          {clients.length === 0 && (
            <Link
              to="/clients/new"
              data-testid="empty-new-client"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2.5 rounded-full mt-4"
            >
              <Plus className="w-4 h-4" strokeWidth={3} /> Nouveau client
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const phoneWa = normalizePhoneForWa(c.phone);
            return (
              <div
                key={c.id}
                data-testid={`client-row-${c.id}`}
                className="bg-white rounded-3xl border border-stone-100 p-4 hover:-translate-y-0.5 hover:shadow-lg transition-all"
              >
                <Link to={`/clients/${c.id}`} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-100 to-stone-200 grid place-items-center shrink-0 font-heading font-black text-stone-700">
                    {c.company_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-black text-base truncate">{c.company_name}</div>
                    {c.contact_name && (
                      <div className="text-sm text-stone-500 truncate">{c.contact_name}</div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" strokeWidth={2.5} />
                </Link>
                {(c.phone || phoneWa) && (
                  <div className="flex gap-2 mt-3">
                    {c.phone && (
                      <a
                        href={`tel:${c.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`quick-call-${c.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-3 py-2 rounded-full active:scale-95 shadow-md shadow-emerald-500/20"
                      >
                        <Phone className="w-3.5 h-3.5" strokeWidth={2.8} /> Appeler
                      </a>
                    )}
                    {phoneWa && (
                      <a
                        href={`https://wa.me/${phoneWa}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`quick-wa-${c.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-xs px-3 py-2 rounded-full active:scale-95 shadow-md"
                      >
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.8} /> WhatsApp
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
